import React from 'react';
import { useTranslation } from 'react-i18next';

import { getJurisdictionLabel } from '../../../../../utils/jurisdiction';
import { getStatusText } from '../../utils';

export const CompanyInfoCard: React.FC<any> = ({ companyInfo }: any) => {
  const { t } = useTranslation();
  
  // Verifica se o nome da empresa já foi escolhido pelo agente
  const isNameChosen = companyInfo?.name && companyInfo.name.trim() !== '';
  
  // Função para formatar status com badge colorido
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'approved': { bg: 'bg-green-100', text: 'text-green-800', label: t('others.othApproved') || 'Aprovado' },
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: t('others.othPending') || 'Pendente' },
      'created': { bg: 'bg-blue-100', text: 'text-blue-800', label: t('others.othCreated') || 'Criado' },
      'paid': { bg: 'bg-green-100', text: 'text-green-800', label: t('others.othCompleted') || 'Pago' },
      'rejected': { bg: 'bg-red-100', text: 'text-red-800', label: t('others.othRejected') || 'Rejeitado' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return (
      <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };
  
  return (
    <div className="space-y-6">
      {/* Header da empresa */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('labels.lblCompanyInfo')}</h2>
        
        {/* Informações principais em cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Registration Number */}
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">{t('labels.lblRegistrationNo')}</span>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-gray-800">
              {companyInfo?.registration_number || (
                <span className="text-gray-400 font-normal">{t('others.othNoRegistrationNumber') || 'Não definido'}</span>
              )}
            </p>
          </div>

          {/* Company Status */}
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">{t('labels.lblCompanyStatus')}</span>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              {getStatusBadge(companyInfo?.status || 'pending')}
            </div>
          </div>

          {/* Incorporation Date */}
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">{t('labels.lblIncorporationDate')}</span>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
          </div>
            <p className="text-lg font-semibold text-gray-800">
              {companyInfo?.incorporation_date || (
                <span className="text-gray-400 font-normal">{t('others.othNoIncorporationDate') || 'Não definido'}</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Detalhes da empresa */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">{t('labels.lblDetails') || 'Detalhes da Empresa'}</h3>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nome Preferido */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0v-4a2 2 0 011-1h4a2 2 0 011 1v4" />
                </svg>
                {t('others.othPreferredName')}
              </label>
              <p className="text-base text-gray-800 font-medium bg-gray-50 rounded-lg px-3 py-2">
                {companyInfo?.ibc_primary || (t('labels.lblNoDataAvailable') || 'Não definido')}
              </p>
            </div>

            {/* Financial Year */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                {t('labels.lblFinancialYear')}
              </label>
              <p className="text-base text-gray-800 font-medium bg-gray-50 rounded-lg px-3 py-2">
                {companyInfo?.financial_year || (t('labels.lblNoDataAvailable') || 'Não definido')}
              </p>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {t('others.othEmailForAgent')}
              </label>
              <p className="text-base text-gray-800 font-medium bg-gray-50 rounded-lg px-3 py-2 break-all">
                {companyInfo?.email || (t('labels.lblNoDataAvailable') || 'Não definido')}
              </p>
            </div>

            {/* Jurisdiction */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('labels.lblJurisdictions')}
              </label>
              <p className="text-base text-gray-800 font-medium bg-gray-50 rounded-lg px-3 py-2">
                {getJurisdictionLabel(companyInfo?.jurisdiction) || (t('labels.lblNoDataAvailable') || 'Não definido')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Alternative Names - só mostra se o nome ainda não foi escolhido */}
      {!isNameChosen && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-amber-50 px-6 py-3 border-b border-amber-200">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('others.othAlternativeNames')}
            </h3>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <label className="text-sm font-medium text-amber-700 mb-2 block">
                  {t('others.othIBCPrimaryName')}
                </label>
                <p className="text-base font-semibold text-gray-800">
                  {companyInfo?.ibc_primary || (t('labels.lblNoDataAvailable') || 'Não definido')}
                </p>
              </div>
              
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <label className="text-sm font-medium text-amber-700 mb-2 block">
                  {t('others.othIBCSecondName')}
                </label>
                <p className="text-base font-semibold text-gray-800">
                  {companyInfo?.ibc_secondary || (t('labels.lblNoDataAvailable') || 'Não definido')}
                </p>
              </div>
              
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <label className="text-sm font-medium text-amber-700 mb-2 block">
                  {t('others.othIBCThirdName')}
                </label>
                <p className="text-base font-semibold text-gray-800">
                  {companyInfo?.ibc_third || (t('labels.lblNoDataAvailable') || 'Não definido')}
                </p>
              </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
};
